//import {Json} from "../../utils";

import {isBrowser} from "../../utils/node.utils";

let log4javascript:any;
if (isBrowser()){
	log4javascript = require('log4javascript/js/log4javascript');
}

const util = require('util');

// enum values must be the same as log4javascript LogLevel
export enum LogLevel{
	ALL,
	TRACE,
	DEBUG,
	INFO,
	WARN,
	ERROR,
	FATAL,
	OFF
}

enum AppenderType {
	ajax,
	browserConsole
}

interface IAppender {
	name:string;
	type:string;
	level:LogLevel;
	batchSize?:number;
	timerInterval?:number;
}

interface ILogger {
	category:string;
	appenders:string[];
	level:LogLevel;
}

export interface ILoggerConfig {
	appenders:IAppender[];
	loggers:ILogger[];
}

interface IDefaultLoggerConfig {
	appender:string;
	appenderLevel:LogLevel;
	loggerLevel:LogLevel;
	batchSize?:number;
	timerInterval?:number;
}

/**
	****************************************************************
	* Class for providing Web Client logging ability.
	*
	* Written by: Stanislav Korobov
	****************************************************************
	*/

// we assume an external load was performed to load log4javascript - the package is not working
// properly in npm.we load from index.html for now

declare var __ris_global_vars__:any;

export class LogManager {
	public static loggerPrefix:string = "WebClient.";
	public static ajaxAppenderServerUrl:string;
	public static ajaxAppenderDetected:boolean = false;
	public static defaultConfigLoaded:boolean = false;
	public static defaultConfigLoading:boolean = false;
	public static credentialsManagerEvent:boolean = false;
	public static loggerSettings:ILoggerConfig = null;
	public static appendersConfigurations:{ [appenderName:string]:any } = {};
	public static offsetDifference:number = 0;

	private static ajaxAppenders:log4javascript.AjaxAppender[] = [];
	//  /**
	//  * Calling the logger configuration after user login.
	//  * @param none
	//  */
	//  public static getUserConfig(logManagerService: ILogManagerService): void {
	//     this.loadLoggerConfig(true, logManagerService, null);
	//  }

	/**
		* Loading the local client default logger configuration and initializing the root logger.
		* If the server is not available and the logger configuration cannot be retrieved,
		* all the logs will be written to the root logger.
		* @param none
		*/
	public static loadDefaultConfig():void {
		this.defaultConfigLoading = true;

		log4javascript.logLog.setQuietMode(true); // disable the internal logger log from showing console messages
		this.ajaxAppenderDetected = false;

		// Initialize root logger with default hard coded settings
		this.initializeRootLogger(AppenderType[AppenderType.browserConsole].valueOf(), LogLevel.WARN, LogLevel.WARN, null, null);

		let data = __ris_global_vars__.loggerDefaults;
		this.initializeRootLogger(data.appender, data.appenderLevel, data.loggerLevel, data.batchSize, data.timerInterval);

		this.defaultConfigLoading = false;
		this.defaultConfigLoaded = true;
	}

	//  /**
	//  * Loading the local client logger configuration and initializing all the loggers.
	//  * @param forUser
	//  * @param config Optional for testing module
	//  */
	//  public static loadLoggerConfig(forUser: boolean, config: string | ILogManagerService, apiURL: string): void {
	//     if (apiURL) {
	//        this.ajaxAppenderServerUrl = apiURL + "/Log/PutLogEntries";//"http://localhost:52178/api/Log/PutLogEntries"
	//     }
	//     // Before loading loggers configuration,
	//     // we check if the root logger is already initialized.
	//     // If not, we initialize it
	//     if (!this.defaultConfigLoaded && !this.defaultConfigLoading) {
	//           this.loadDefaultConfig();
	//     }
	//     // For test purposes. We don't need to get the logger configuration from server. We already got it from testing module.
	//     if (typeof config === 'string') {
	//        this.loggerSettings = <ILoggerConfig>JSON.parse(config);
	//        this.parseConfig(this.loggerSettings);
	//     } else { // must be if (config instanceof LogManagerService) {
	//        // get the logger configuration from server
	//        let logMS = config;
	//        let promisedConfig: IHttpPromise<ILoggerConfig>;

	//        if (forUser) {
	//           promisedConfig = logMS.getUserData();
	//        } else {
	//           promisedConfig = logMS.getData();
	//        }

	//        promisedConfig.then((data: ILoggerConfig) => {
	//           this.debug(log4javascript.getRootLogger().name, "Logger configuration successfully retrieved from server.", data);
	//           this.parseConfig(data);
	//        }, (data) => {
	//           this.error(log4javascript.getRootLogger().name, "Error occured while loading logger configuration from server.", data);
	//        });
	//     }
	//  }

	/**
		* Methods to log message.
		* @param category The name of the logger.
		* @param message Message to be logged.
		* @param params Array of additional parameters like errors and objects.
		*/

	// Debug level.
	public static debug(category:string, message:string, ...params):void {
		if (isBrowser()){
			this.myLog(category, log4javascript.Level.DEBUG, message, params);
		}else{
			console.log(message);
		}
	}

	// Info level.
	public static info(category:string, message:string, ...params):void {
		if (isBrowser()) {
			this.myLog(category, log4javascript.Level.INFO, message, params);
		}else{
			console.log(message);
		}
	}

	// Warn level.
	public static warn(category:string, message:string, ...params):void {
		if (isBrowser()) {
			this.myLog(category, log4javascript.Level.WARN, message, params);
		}else{
			console.log(message);
		}
	}

	// Error level.
	public static error(category:string, message:string, ...params):void {
		if (isBrowser()) {
			this.myLog(category, log4javascript.Level.ERROR, message, params);
		}else{
			console.log(message);
			if (params){
				params.forEach(p=>{console.log(p);});
			}
		}
	}

	// Fatal level.
	public static fatal(category:string, message:string, ...params):void {
		if (isBrowser()) {
			this.myLog(category, log4javascript.Level.FATAL, message, params);
		}else{
			console.log(message);
		}
	}

	// For debug level.
	public static isDebugEnabled(category:string):boolean {
		if (isBrowser()) {
			return this.isEnabledFor(category, log4javascript.Level.DEBUG);
		}else{
			return true;
		}
	}

	// For info level.
	public static isInfoEnabled(category:string):boolean {
		if (isBrowser()) {
			return this.isEnabledFor(category, log4javascript.Level.INFO);
		}else{
			return true;
		}
	}

	// For warn level.
	public static isWarnEnabled(category:string):boolean {
		if (isBrowser()) {
			return this.isEnabledFor(category, log4javascript.Level.WARN);
		}
	}

	// For error level.
	public static isErrorEnabled(category:string):boolean {
		if (isBrowser()) {
			return this.isEnabledFor(category, log4javascript.Level.ERROR);
		}else{
			return true;
		}
	}

	// For fatal level.
	public static isFatalEnabled(category:string):boolean {
		if (isBrowser()) {
			return this.isEnabledFor(category, log4javascript.Level.FATAL);
		}else{
			return true;
		}
	}

	public static parseLoadedConfig(data:ILoggerConfig, apiUrl:string):void {
		this.ajaxAppenderServerUrl = apiUrl + "/Log/PutLogEntries";//"http://localhost:52178/api/Log/PutLogEntries"
		this.parseConfig(data);
	}

	/**
	 * Sets root logger to debug mode.
     */
	public static activateBrowserConsoleLogger(level:LogLevel): void {
		this.initializeRootLogger(AppenderType[AppenderType.browserConsole].valueOf(), level, level, null, null);
	}

	/**
		* Logging message.
		* @param category The name of the logger.
		* @param level The level of the logger.
		* @param message Message to be logged.
		* @param params Array of additional parameters like errors and objects.
		*/

	/**
		* Initializing all the appenders and loggers.
		* @param data Logger configuration
		*/
	public static parseConfig(data:ILoggerConfig):void {
		this.loggerSettings = data;

		// fill the list of appenders
		this.appendersConfigurations = {}; // Clear the list of all appenders
		this.ajaxAppenderDetected = false;

		if ((this.loggerSettings) && (this.loggerSettings.appenders)) {
			for (let i = 0; i < this.loggerSettings.appenders.length; i++) {
				let currAppenderSetting:IAppender = this.loggerSettings.appenders[i];
				if (currAppenderSetting.type) {
					// Initializing appender
					let appender = this.initializeAppender(currAppenderSetting.type);

					// Set logging level for current appender
					if ((currAppenderSetting.level)) {
						appender.setThreshold(this.initializeLevel(currAppenderSetting.level));
					}
					// ajax handling
					if (currAppenderSetting.type === AppenderType[AppenderType.ajax].valueOf()) {
						const ajaxAppender: log4javascript.AjaxAppender = appender as log4javascript.AjaxAppender;
						ajaxAppender.setSendAllOnUnload(true);
						(ajaxAppender).setWaitForResponse(true); // Set wait for response
						(ajaxAppender).setFailCallback(message => this.failCallback(message)); // Set callback actions
						(ajaxAppender).setRequestSuccessCallback(request => this.requestSuccessCallback(request));

						// Set batchsize for Ajax appender
						if (currAppenderSetting.batchSize) {
							(ajaxAppender).setBatchSize(currAppenderSetting.batchSize);
						}

						// Set timer interval for Ajax appender
						if (currAppenderSetting.timerInterval) {
							(ajaxAppender).setTimed(true);
							(ajaxAppender).setTimerInterval(currAppenderSetting.timerInterval);
						}
					}
					this.appendersConfigurations[currAppenderSetting.name] = {appender};
				}
			}
		}

		// initialize loggers
		if ((this.loggerSettings) && (this.loggerSettings.loggers)) {
			for (let i = 0; i < this.loggerSettings.loggers.length; i++) {
				let currLoggerSetting:ILogger = <ILogger> this.loggerSettings.loggers[i];
				let logger:log4javascript.Logger =
					(currLoggerSetting.category) &&
					(currLoggerSetting.category !== "")  &&
					(currLoggerSetting.category !== this.loggerPrefix+"root")	?
					log4javascript.getLogger(currLoggerSetting.category) : log4javascript.getRootLogger();

				if (currLoggerSetting.level) {
					logger.setLevel(this.initializeLevel(currLoggerSetting.level)); // Setting logger level
				}
				logger.setAdditivity(true);
				logger.removeAllAppenders();
				if (currLoggerSetting.appenders) {
					for (let j = 0; j < currLoggerSetting.appenders.length; j++) {
						if ((this.appendersConfigurations) &&
							(this.appendersConfigurations[currLoggerSetting.appenders[j]])) {
							logger.addAppender(this.appendersConfigurations[currLoggerSetting.appenders[j]].appender);
						}
					}
				}
			}
		}
	}

	/**
		* Action involved after Ajax appender fails to send messages to the log server.
		* @param message Text message about the send failure.
		*/
	public static failCallback(message:string):void {
		console.error(message);
	}

	/**
		* Action involved after Ajax appender succeeds to send messages to the log server.
		* @param request Request information.
		*/
	public static requestSuccessCallback(request:XMLHttpRequest):void {
		// notice the response is successful even if the server is not available at all. this is due to CORS known issue
		// reporting status 0 for failed cors requests.
		console.debug("Response received from server for ajax logger appender. Response text - " + request.responseText);
	}

	/**
		* Initializing the root logger.
		* @param appender The type of the appender.
		* @param level The active logging level.
		*/
	public static initializeRootLogger(appender:string, appenderLevel:LogLevel, loggerLevel:LogLevel, batchSize?:number, timerInterval?:number):void {
		try {
			let rootLogger:log4javascript.Logger = log4javascript.getRootLogger();
			// Reset all current appenders
			rootLogger.removeAllAppenders();

			// Set additivity and logger level
			rootLogger.setAdditivity(false);
			rootLogger.setLevel(this.initializeLevel(loggerLevel));

			// Set appender
			let _appender = this.initializeAppender(appender);

			// Set appender level
			_appender.setThreshold(this.initializeLevel(appenderLevel));

			// ajax config
			// Set batch size for Ajax appender
			if (appender === AppenderType[AppenderType.ajax].valueOf()) {
				const ajaxAppender = _appender as log4javascript.AjaxAppender;
				this.ajaxAppenders.push(ajaxAppender);

				ajaxAppender.setSendAllOnUnload(true);
				ajaxAppender.setWaitForResponse(true); // Set wait for response
				ajaxAppender.setFailCallback(message => this.failCallback(message)); // Set callback actions
				ajaxAppender.setRequestSuccessCallback(request => this.requestSuccessCallback(request));

				if (batchSize > 0) {
					ajaxAppender.setBatchSize(batchSize);
				}

				// Set timer interval for Ajax appender
				if (timerInterval) {
					ajaxAppender.setTimed(true);
					ajaxAppender.setTimerInterval(timerInterval);
				}
			}

			rootLogger.addAppender(_appender);

			this.debug(rootLogger.name, "Default logger successfully initialized.");
		} catch (err) {
			console.error("Unabled to initialize root logger.", err);
		}
	}

	/**
		* Initializing logger's appender.
		* @param appender The name of the appender received from the configuration.
		* @returns Initialized appender.
		*/
	public static initializeAppender(appender:string):log4javascript.Appender {
		let _appender:log4javascript.Appender;
		let _layout:log4javascript.Layout;

		switch (appender) {
			case AppenderType[AppenderType.ajax].valueOf():
				this.ajaxAppenderDetected = true;
				_appender = new log4javascript.AjaxAppender(this.ajaxAppenderServerUrl);
				(<log4javascript.AjaxAppender> _appender).addHeader("Content-Type", "application/json");
				_layout = new log4javascript.JsonLayout();
				_appender.setLayout(_layout);
				break;
			case AppenderType[AppenderType.browserConsole].valueOf():
				_appender = new log4javascript.BrowserConsoleAppender();
				_layout = new log4javascript.NullLayout();
				_appender.setLayout(_layout);
				break;
			default:
				_appender = new log4javascript.BrowserConsoleAppender();
				_layout = new log4javascript.NullLayout();
				_appender.setLayout(_layout);
		}
		return _appender;
	}

	/**
		* Initializing logger's logging level.
		* @param level The name of the level received from the configuration.
		* @returns Initialized level.
		*/
	public static initializeLevel(level:LogLevel):any {
		return log4javascript.Level[LogLevel[level].valueOf()];
	}

	/**
		* Check if an appender is of AjaxAppender type.
		* @param appender Appender to check.
		* @returns Appender of AjaxAppender type.
		*/
	public static isAjaxAppender(appender) {
		return appender.valueOf().toString() === "AjaxAppender";// (<any>log4javascript.AjaxAppender).name;
	}

	/**
		* Analog of C# 'String.format()' function.
		* @param format A format string.
		* @param values An array of objects to be placed in the formatted string.
		* @returns Concatinated formatted string with inserted objects.
		*/
	public static stringFormatter(format:string, values:any[]):string {
		return format.replace(/{(\d+)}/g, (match, number) => {
			return values[number] ? this.getObjectString(values[number]) : match;
		});
	}

	/**
		* Serializing exceptions and objects.
		* @param obj An object to be serialized.
		* @returns Serialized object.
		*/
	public static getObjectString(obj:any) {
		obj = obj || 'NullUndefined';
		if (obj.name === "Error") {
			return "\n error message: ".concat(obj.message, ";\n error stack: ", obj.stack);
		} else {
			return "\n object name: ".concat((<any> (<Object> obj).constructor).name, ";\n object content: ", util.inspect(obj));
		}
	}

	/**
		* Methods to check if the logger is enabled for the specified level.
		* @param category The name of the logger.
		* @returns Whether the logger is enabled for the specified level.
		*/


	/**
		* Generic check for the logger level.
		* @param category The name of the logger.
		* @param level The logging level.
		* @returns Whether the logger is enabled for the specified level.
		*/
	public static isEnabledFor(category:string, level:log4javascript.Level):boolean {
		if (!category.startsWith(this.loggerPrefix)){
			category = this.loggerPrefix + category;
		}
		let logger = log4javascript.getLogger(category);
		if (logger) {
			return logger.isEnabledFor(level, null);
		} else {
			return false;
		}
	}

	public static log(category:string, level:LogLevel, message:string, params:any[]):void {
		let  log4JLevel:log4javascript.Level;
		log4JLevel=this.log4NetLevelFromLogLevel(level);
		this.myLog(category,log4JLevel,message,params);
	}
	private static  log4NetLevelFromLogLevel( level:LogLevel):log4javascript.Level{
		switch (level) {
			case LogLevel.ALL:
				return log4javascript.Level.ALL;
			case LogLevel.DEBUG:
				return log4javascript.Level.DEBUG;
			case LogLevel.ERROR:
				return log4javascript.Level.ERROR;
			case LogLevel.FATAL:
				return log4javascript.Level.FATAL;
			case LogLevel.INFO:
				return log4javascript.Level.INFO;
			case LogLevel.OFF:
				return log4javascript.Level.OFF;
			case LogLevel.TRACE:
				return log4javascript.Level.TRACE;
			case LogLevel.WARN:
				return log4javascript.Level.WARN;
		}
		throw new Error("unknown log level type");
	}
	private static myLog(category:string, level:log4javascript.Level, message:string, params:any[]):void {
		// First of all, we check if the root logger is already initialized.
		// If not, we initialize it
		if (!this.defaultConfigLoaded && !this.defaultConfigLoading) {
			this.loadDefaultConfig();
		}

		category = this.loggerPrefix + category;
		let logger = log4javascript.getLogger(category);
		if (logger) {
			if (!(logger.isEnabledFor(level,null))) {
				return;
			}
			// Check if we didn't get a null message
			if (!message) {
				message = "";
			}

			let d = new Date();
			if (this.offsetDifference) {
				d.setTime(d.getTime() - this.offsetDifference*60*1000);
			}
			message = d.toLocaleString().concat('[', '' + d.getMilliseconds(), '] ', category, ' ', level.toString(), ' ', ' - ', message);
			// Check if current logger has an AjaxAppender
			let filteredAppenders;
			if (this.ajaxAppenderDetected) {
				filteredAppenders = (<any> logger).getEffectiveAppenders().filter(this.isAjaxAppender);
			}

			// AjaxAppender doesn't support objects, so we have to serialize all errors and objects
			if ((filteredAppenders) && (filteredAppenders.length > 0)) {
				// Message serialization is an expensive process, so we check prior if the logger is enabled at all for this logging level
				if (this.isEnabledFor(category, level)) {
					for (let i = 0; i < params.length; i++) {
						message = message.concat(" ", this.getObjectString(params[i]));
					}
					logger.log(level, [message]); // Log with a serialized errors and objects
				}
			} else {
				if (params.length > 0) {
					logger.log(level, [message, params]); // Log in a regular way to browser console with objects
				} else {
					logger.log(level, [message]);
				}
			}
		} else {
			console.error("Failed to get logger " + category);
		}
	}
}


